import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { EChartsOption } from "echarts";

interface sensorItem {
  id: string;
  name: string;
  critical: number;
  cautious: number;
  warning: number;
  riverData: any;
  riverName: string;
  fullScreen: boolean;
  lastUpdate: string | null;
  showDialog: boolean;
  dimensions: {
    resizableBoxwidth: number;
    resizableBoxheight: number;
  };
  chartWidth: number;
  chartHeight: number;
  onDataLoaded: (loaded: boolean) => void;
  predictValues: React.Dispatch<
    React.SetStateAction<
      {
        id: number;
        name: string;
        value: number;
        timestamp: string;
      }[]
    >
  >;
  selectedModel:string;
}

const MainForcastChart: React.FC<sensorItem> = ({
  id,
  name,
  critical,
  cautious,
  warning,
  riverData,
  riverName,
  fullScreen,
  lastUpdate,
  showDialog,
  dimensions,
  chartWidth,
  chartHeight,
  onDataLoaded,
  predictValues,
  selectedModel
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>();
  const [initialData, setInitialData] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [dataLoaded, setDataLoaded] = useState<boolean>(true);
  const [values, setValues] = useState<
    { id: number; name: string; value: number; timestamp: string }[]
  >([]);
  const [predictionData, setPredictionData] = useState<any[]>([]);

  useEffect(() => {
    const fetchDataAndInitializeChart = async (retryCount = 0) => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 1000; // 1 second

      try {
        const response = await fetch(`https://demo.ml.eaglei.live/predict`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ selectedSensorID: id }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();

        if (responseData && Array.isArray(responseData.results)) {
          const data = responseData.results.map((item: any) => [
            item.timestamp,
            item.value,
          ]);
          // setInitialData(data);
          setInitialData(data);
          setDataLoaded(false);

          const formattedData = responseData.results.map(
            (item: any, index: number) => ({
              id: index,
              name: item.id,
              value: item.value,
              timestamp: item.timestamp,
            })
          );
          setValues(formattedData);
        } else {
          console.error("Invalid response data format:", responseData);
          if (retryCount < MAX_RETRIES) {
            setTimeout(
              () => fetchDataAndInitializeChart(retryCount + 1),
              RETRY_DELAY
            );
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (retryCount < MAX_RETRIES) {
          setTimeout(
            () => fetchDataAndInitializeChart(retryCount + 1),
            RETRY_DELAY
          );
        } else {
          setDataLoaded(true);
        }
      }
    };

    fetchDataAndInitializeChart();
  }, [id,selectedModel]);

  useEffect(() => {
    if (dataLoaded) {
      onDataLoaded(true);
    } else {
      onDataLoaded(false);
    }
  }, [dataLoaded]);

  useEffect(() => {
    if (values && values.length > 0) {
      const lastSixValues = values.slice(-6); // Get the last 6 elements
      predictValues(lastSixValues);
    }
  }, [values]);

  useEffect(() => {
    if (
      chartRef.current &&
      chartRef.current.clientWidth &&
      chartRef.current.clientHeight
    ) {
      const chart = echarts.init(chartRef.current);

      const values = initialData.map((item) => item[1]);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);

      const yMin = parseFloat((minValue - 0.1).toFixed(2));
      const yMax = parseFloat((maxValue + 0.1).toFixed(2));

      const chartOption: EChartsOption = {
        color: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00"],
        title: {
          left: "center",
          text: "Next 6 hours",
          textStyle: { fontSize: "0.8rem", color: "#FFFFFF" },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: [name],
          textStyle: {
            color: "#FFFFFF",
          },
          right: "10%",
          top: "10%",

          backgroundColor: "rgba(0,0,0,0.5)",
          padding: [5, 10],
          borderRadius: 4,
          itemGap: 10,
          itemWidth: 20,
          itemHeight: 10,
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: "none",
            },
            restore: {},
            saveAsImage: {},
          },
        },
        grid: {
          left: "3%",
          right: "14%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          type: "time",
          name: "Time",
          axisLabel: {
            color: "#FFFFFF",
          },

          // boundaryGap: [0, "100%"],
          axisLine: {
            lineStyle: {
              color: "#FFFFFF",
              width: 2,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "Water Level",
          nameTextStyle: {
            color: "#FFFFFF",
          },
          axisLabel: {
            formatter: "{value} m",
            color: "#FFFFFF",
          },
          // boundaryGap: [0, "100%"],
          min: yMin,
          max: yMax,
          axisLine: {
            lineStyle: {
              color: "#070807",
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              color: "rgba(85, 84, 95, 0.6)",
              width: 1,
            },
          },
          // ...splitLineStyle,
        },

        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
          {
            start: 0,
            end: 100,
          },
        ],
        visualMap: {
          type: "piecewise",
          show: false,
          dimension: 1,
          pieces: [
            {
              gt: 0,
              lt: cautious,
              color: "rgba(105,125,194,0.9)",
            },
            {
              gt: cautious,
              lt: warning,
              color: "#ffff00",
            },
            {
              gt: warning,
              lt: critical,
              color: "#ff8c00",
            },
            {
              gt: critical,
              color: "#f00e0e",
            },
          ],
        },
        series: [
          {
            name: name,
            type: "line",
            stack: "Total",
            smooth: true,
            symbol: "diamond",
            lineStyle: {
              width: 2,
              color: "#33FF57",
            },
            areaStyle: {
              opacity: 0.8,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: "rgba(51, 255, 87, 0.8)",
                },
                {
                  offset: 1,
                  color: "rgba(51, 255, 189, 0.2)",
                },
              ]),
              // color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              //         {
              //           offset: 0,
              //           color: "rgba(255, 51, 255, 0.8)",
              //         },
              //         {
              //           offset: 1,
              //           color: "rgba(255, 51, 153, 0.2)",
              //         },
              //       ]),
            },
            emphasis: {
              focus: "series",
            },
            data: initialData,
            markLine: {
              symbol: "circle",
              data: [
                {
                  name: "Danger",
                  yAxis: critical,
                  itemStyle: {
                    color: "red",
                  },
                  label: {
                    formatter: " Danger Level",
                  },
                },
                {
                  name: "Warning",
                  yAxis: warning,
                  itemStyle: {
                    color: "orange",
                  },
                  label: {
                    formatter: " Warning Level",
                  },
                },
                {
                  name: "Cautious",
                  yAxis: cautious,
                  itemStyle: {
                    color: "yellow",
                  },
                  label: {
                    formatter: " Cautious Level",
                  },
                },
              ],
            },
            markArea: {
              itemStyle: {
                color: "rgba(255, 173, 177, 0.4)",
              },
              data:
                initialData && initialData.length > 0
                  ? [
                      [
                        {
                          xAxis:
                            initialData.length >= 6
                              ? initialData[initialData.length - 6][0]
                              : initialData[0][0],
                        },
                        {
                          xAxis:
                            initialData.length > 0
                              ? initialData[initialData.length - 1][0]
                              : initialData[0][0],
                        },
                      ],
                    ]
                  : [],
            },
          },
        ],
      };

      chart.setOption(chartOption);
      setChartInstance(chart);
    }

    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, [initialData]);

  useEffect(() => {
    if (chartInstance) {
      setTimeout(() => {
        chartInstance.resize();
      }, 100);
    }
  }, [fullScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartInstance]);

  useEffect(() => {
    if (chartWidth !== 0) {
      setInitialLoad(false);
    }
  }, [chartWidth, initialLoad]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [chartWidth, chartHeight]);

  return (
    <div
      id="main"
      style={{
        width: initialLoad ? "100%" : `${chartWidth}px`,
        height: initialLoad ? "40vh" : `${chartHeight}px`,
      }}
      ref={chartRef}
    ></div>
  );
};

export default MainForcastChart;
